//import 'chartjs-plugin-zoom';
import { action, computed, observable, toJS } from 'mobx';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartTypeRegistry,
    TooltipModel,
    TooltipItem,
    ChartEvent,
    LegendItem,
    ChartData,
    ScaleOptionsByType,
    CartesianScaleTypeRegistry,
    LegendOptions,
    ChartOptions,
    TimeScale,
    Chart,
} from 'chart.js';
import { observer } from 'mobx-react';
import { getDatasetAtEvent, Line } from 'react-chartjs-2';
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';
import { bind } from '../Utils/Bind';
import { componentAutoReaction } from '../Utils/MobxTools';
import * as React from 'react';
import { Data } from '@react-google-maps/api';

export interface IActiveElement {
    datasetIndex: number;
    element: PointElement;
    index: number;
}

export interface ILineGraphProps {
    tooltipLabelCallback?(
        this: TooltipModel<keyof ChartTypeRegistry>,
        tooltipItem: TooltipItem<keyof ChartTypeRegistry>
    ): string | string[];
    onClick?(dataset: {}[], event: React.MouseEvent<HTMLCanvasElement>): void;
    onLegendClick?(event: ChartEvent, legendItem?: LegendItem): void;
    readonly displayLabels?: boolean;
    readonly data: ChartData<'line'>;
    readonly title?: string;
    readonly loading?: boolean;
    readonly yMin?: number;
    readonly yMax?: number;
    readonly hideYaxis?: boolean;
    readonly hideXaxis?: boolean;
    readonly hideLines?: boolean;
    readonly hideTooltips?: boolean;
    readonly className?: string;
    readonly graphContainer?: string;
    readonly height?: number;
    readonly width?: number;
    readonly hideDots?: boolean;
    readonly redraw?: boolean;
    readonly legendPosition?: 'top' | 'left' | 'bottom' | 'right' | undefined;
    readonly yAxes?: { [key: string]: ScaleOptionsByType<keyof CartesianScaleTypeRegistry> };
    readonly legendOptions?: LegendOptions<'line'>;
    readonly filterLegend?: { item: LegendItem };
    readonly enableZoom?: boolean;
    readonly enablePan?: boolean;
    readonly fontColor?: string;
    readonly gridColor?: string;
    readonly ignoreAspectRatio?: boolean;
    readonly hideXLines?: boolean;
    readonly timeScale?: boolean;
}

ChartJS.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, Title, Tooltip, Legend);

@observer
export class LineGraph extends React.Component<ILineGraphProps> {
    constructor(props: ILineGraphProps) {
        super(props);
        this.chartRef = React.createRef();
    }

    public render() {
        return (
            <div className={this.props.className + ' d-flex flex-grow-1 graph-container'}>
                {this.props.title && <p>{this.props.title}</p>}

                <div className={this.props.graphContainer + ' graph-container'}>
                    <Line
                        data={toJS(this.props.data)}
                        options={this.options}
                        redraw={this.props.redraw}
                        onClick={event => {
                            this.getDatasetOnClick(event);
                        }}
                        ref={this.chartRef}
                        height={this.props.height}
                        width={this.props.width}
                    />
                </div>
            </div>
        );
    }

    @bind
    @action
    private getDatasetOnClick(event: React.SyntheticEvent<HTMLCanvasElement, Event>) {
        const current = (this.chartRef as any).current;
        const dataset = getDatasetAtEvent(current, event as React.MouseEvent<HTMLCanvasElement, MouseEvent>);

        if (this.props.onClick) {
            this.props.onClick(dataset, event as React.MouseEvent<HTMLCanvasElement, MouseEvent>);
        }
    }

    @computed
    private get options(): ChartOptions<'line'> {
        const options: ChartOptions<'line'> = {
            normalized: true,
            elements: {
                point: {
                    hitRadius: 12,
                    radius: this.props.hideDots ? 0 : 1,
                },
            },
            animation: false,
            plugins: {
                legend: {
                    display: this.props.displayLabels,
                    position: this.props.legendPosition ?? 'top',
                    //onClick: this.props.onLegendClick,
                    labels: {
                        color: this.props.fontColor,
                        boxWidth: 15,
                        font: {
                            size: 14,
                        },
                    },
                },
                tooltip: {
                    enabled: true,
                    mode: 'index',
                    position: 'nearest',
                    callbacks: {
                        // label: this.props.tooltipLabelCallback,
                    },
                },
            },
            scales: {
                y: !this.props.yAxes
                    ? {
                        display: !this.props.hideYaxis,
                        ticks: {
                            color: this.props.fontColor,
                            display: !this.props.hideYaxis,
                        },
                        min: this.props.yMin,
                        max: this.props.yMax,
                        grid: {
                            color: this.props.gridColor,
                            display: !this.props.hideLines,
                            drawBorder: !this.props.hideLines,
                        },
                    }
                    : { display: false },
                ...this.props.yAxes,
                x: {
                    display: !this.props.hideXaxis,
                    type: this.props.timeScale ? 'time' : undefined,
                    time: this.props.timeScale
                        ? {
                            tooltipFormat: 'M/d/yy h:mm a',
                        }
                        : undefined,
                    ticks: {
                        color: this.props.fontColor,
                        display: true,
                        major: {
                            enabled: true,
                        },
                    },
                    grid: {
                        color: this.props.gridColor,
                        display: !(this.props.hideLines ?? this.props.hideXLines),
                        drawBorder: !this.props.hideLines,
                    },
                },
            },
            //pan: {
            //    // Boolean to enable panning
            //    enabled: this.props.enablePan,

            //    // Panning directions. Remove the appropriate direction to disable
            //    // Eg. 'y' would only allow panning in the y direction
            //    mode: 'xy',
            //},

            // Container for zoom options
            //zoom: {
            //    // Boolean to enable zooming
            //    enabled: this.props.enableZoom,

            //    // Enable drag-to-zoom behavior
            //    drag: true,

            //    // Zooming directions. Remove the appropriate direction to disable
            //    // Eg. 'y' would only allow zooming in the y direction
            //    mode: 'xy',
            //},
            maintainAspectRatio: !this.props.ignoreAspectRatio,
            responsive: true,
        };

        if (options.animation == undefined) delete options.animation;

        if (options.plugins?.tooltip?.callbacks?.label == undefined) delete options.plugins?.tooltip?.callbacks?.label;

        //if (options.scales?.yAxes?.) {
        //    if (options.scales.yAxes[0].ticks?.callback == undefined) delete options.scales.yAxes![0].ticks!.callback;
        //}

        //if (options.scales.xAxes?.length) {
        //    if (options.scales.xAxes[0].ticks?.callback == undefined) delete options.scales.xAxes![0].ticks!.callback;

        //    if (options.scales.xAxes[0].type == undefined) delete options.scales.xAxes![0].type;

        //    if (options.scales.xAxes[0].time == undefined) delete options.scales.xAxes![0].time;

        //    if (options.scales.xAxes[0].scaleLabel == undefined) delete options.scales.xAxes![0].scaleLabel;
        //}

        return options as any;
    }

    @observable
    private chartRef: React.ForwardedRef<ChartJSOrUndefined<'line'>> | undefined = undefined;
}
